import { styled } from '@linaria/react';
import type { KeyboardEvent, MouseEvent } from 'react';

/** Pixels each Left/Right arrow press grows or shrinks the panel. */
const KEYBOARD_STEP = 16;

interface FloraResizeHandleProps {
  /** Begins a drag — wire to the resize hook's `handleMouseDown`. */
  onMouseDown: (event: MouseEvent) => void;
  /** Whether a drag is in progress; drives the active highlight. */
  isDragging: boolean;
  /**
   * Keyboard resize callback. Receives a signed pixel delta — positive widens
   * the panel (handle moves left), negative narrows it. Omit to disable
   * keyboard resizing.
   */
  onResize?: (deltaX: number) => void;
  /** Current panel width (px) for `aria-valuenow`. */
  width?: number;
  /** Minimum panel width (px) for `aria-valuemin`. */
  minWidth?: number;
  /** Maximum panel width (px) for `aria-valuemax`. */
  maxWidth?: number;
  /**
   * Which edge of the panel the handle sits on. `'left'` (default) is the
   * right-docked aside; `'right'` is a left-docked panel (e.g. the page-viewer),
   * where dragging/arrowing right widens instead of left.
   */
  edge?: 'left' | 'right';
}

/**
 * Draggable resize affordance for the Flora panel. Drag (or focus + Left/Right
 * arrows) widens or narrows the panel. Defaults to the right-docked aside (handle
 * on the left edge, dragging left widens); pass `edge="right"` for a left-docked
 * panel (handle on the right edge, dragging right widens).
 *
 * Mirrors the flora-agent artifacts divider: a `col-resize` strip with an
 * enlarged hitbox and a grip that highlights on hover/drag. Exposed as an ARIA
 * separator so the resize is keyboard-operable.
 *
 * @param {FloraResizeHandleProps} props - Drag/keyboard handlers, ARIA value bounds, and dock edge
 * @returns {JSX.Element} The resize handle element
 */
export function FloraResizeHandle({
  onMouseDown,
  isDragging,
  onResize,
  width,
  minWidth,
  maxWidth,
  edge = 'left',
}: FloraResizeHandleProps) {
  // On a left-docked panel the widen/narrow arrow keys flip: dragging the
  // right-edge handle rightward widens, so ArrowRight must widen there.
  const widenKey = edge === 'right' ? 'ArrowRight' : 'ArrowLeft';
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!onResize) return;
    if (event.key === widenKey) {
      event.preventDefault();
      onResize(KEYBOARD_STEP);
    } else if (event.key === (widenKey === 'ArrowLeft' ? 'ArrowRight' : 'ArrowLeft')) {
      event.preventDefault();
      onResize(-KEYBOARD_STEP);
    }
  };

  return (
    <HandleBar
      edge={edge}
      data-bf="flora-resize-handle"
      data-dragging={isDragging}
      onMouseDown={onMouseDown}
      onKeyDown={handleKeyDown}
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize Flora panel"
      aria-valuenow={width}
      aria-valuemin={minWidth}
      aria-valuemax={maxWidth}
      tabIndex={0}
    >
      <Grip />
    </HandleBar>
  );
}

const HandleBar = styled.div<{ edge: 'left' | 'right' }>`
  position: absolute;
  left: ${(props) => (props.edge === 'right' ? 'auto' : '0')};
  right: ${(props) => (props.edge === 'right' ? '0' : 'auto')};
  top: 0;
  bottom: 0;
  width: 14px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: col-resize;
  background: transparent;
  transition: background 0.15s;

  /* Enlarge the grab area only OUTWARD (into the gap beside the panel) — never
     inward into live panel content, which would steal mousedown from edge
     controls. The 14px strip sits within the panel's >=16px edge padding, so the
     centered grip gets breathing room from the edge without overlapping content. */
  &::before {
    content: '';
    position: absolute;
    inset: ${(props) => (props.edge === 'right' ? '0 -4px 0 0' : '0 0 0 -4px')};
  }

  &:hover,
  &[data-dragging='true'] {
    background: var(--secondary-lightest);
  }

  /* Highlight the nested grip (the styled.span child) while hovered or dragging.
     Target the child span directly rather than interpolating the Grip styled
     component as a selector: wyw-in-js@2 (Oxc) tree-shakes a styled component
     whose only reference is such an interpolation, dropping its declaration
     while keeping its JSX usage and causing a ReferenceError at render. */
  &:hover > span,
  &[data-dragging='true'] > span {
    background: var(--secondary-base);
  }

  &:focus-visible {
    outline: 2px solid var(--secondary-base);
    outline-offset: -1px;
  }
`;

const Grip = styled.span`
  width: 4px;
  height: 32px;
  border-radius: 2px;
  background: var(--ink-lighter);
  pointer-events: none;
  transition: background 0.15s;
`;
